import { useAuth } from './useAuth';
import { useImpersonation } from '../context/useImpersonation';
import { FEATURE_GATES, PRACTICE_FEATURE_FLAGS, type FeatureFlag } from '@/config/features';
import { usePracticeFeatureFlags } from './usePracticeFeatureFlags';

export interface FeatureFlagState {
  enabled: boolean;
  /** True while a practice-stored flag is still being fetched — treat as "unknown", not "off". */
  isLoading: boolean;
}

export function useFeatureFlagState(flag: FeatureFlag): FeatureFlagState {
  const { user } = useAuth();
  const { impersonatedPractice } = useImpersonation();
  const { flags, isLoading } = usePracticeFeatureFlags();
  if (!user) return { enabled: false, isLoading: false };
  const practiceId = impersonatedPractice?.id ?? user.practiceId ?? '';

  const storedKey = PRACTICE_FEATURE_FLAGS[flag];
  if (storedKey) {
    // Off by default: only an explicit `true` on the practice record enables it.
    if (isLoading) return { enabled: false, isLoading: true };
    return { enabled: flags?.[storedKey] === true, isLoading: false };
  }
  const gate = FEATURE_GATES[flag];
  // '*' enables the flag for every practice; otherwise it's an allow-list of practice ids.
  return { enabled: gate.includes('*') || gate.includes(practiceId), isLoading: false };
}

export function useFeatureFlag(flag: FeatureFlag): boolean {
  return useFeatureFlagState(flag).enabled;
}
